import React, { useState } from "react";
import { 
  FiShield, 
  FiFileText, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiExternalLink,
  FiRefreshCw
} from "react-icons/fi";
import type { FmeaReference } from "../../../../types/rca.types.js";

export type FmeaReferencePanelProps = {
  rcaRecordId?: string;
  problemStatement?: string;
  readOnly?: boolean;
  onUpdateFmeaTriggered?: () => void;
};

const INITIAL_FMEA: FmeaReference = {
  id: "fmea-1",
  rcaRecordId: "rca-current",
  fmeaType: "PFMEA",
  documentNumber: "PFMEA-2026-001",
  revision: "Rev 4",
  failureMode: "Dimension Deviation (+0.08mm)",
  currentSeverity: 8,
  currentOccurrence: 6,
  currentDetection: 7,
  actionPriority: "HIGH",
  updateRequired: true,
  updateReason: "Root cause revealed calibration alert gap in ERP PM. PFMEA must increase Occurrence rating and introduce automated error-proofing.",
  assignedChangeOwner: "Arun (Engineering)",
  status: "REVIEW_PENDING",
};

export function FmeaReferencePanel({
  rcaRecordId = "rca-current",
  problemStatement,
  readOnly = false,
  onUpdateFmeaTriggered,
}: FmeaReferencePanelProps) {
  const [fmea, setFmea] = useState<FmeaReference>(INITIAL_FMEA);
  const [isSaved, setIsSaved] = useState<boolean>(false);

  const handleTriggerUpdate = () => {
    setIsSaved(true);
    onUpdateFmeaTriggered?.();
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-teal-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            FMEA
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              18. FMEA Reference (Supporting Risk Feedback)
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                AIAG & VDA AP Integration
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Link confirmed root cause learnings back into process and design risk assessments (PFMEA / DFMEA).
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleTriggerUpdate}
          className="px-3.5 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
        >
          <FiRefreshCw className="w-3.5 h-3.5" />
          Review / Update FMEA
        </button>
      </div>

      {isSaved && (
        <div className="px-6 py-2.5 bg-emerald-50 text-emerald-800 border-b border-emerald-200 text-xs flex items-center gap-2">
          <FiCheckCircle className="w-4 h-4 text-emerald-600" />
          <span>FMEA revision workflow initiated. Engineering change task dispatched to {fmea.assignedChangeOwner}.</span>
        </div>
      )}

      {/* Grid Properties */}
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50">
            <span className="text-[11px] text-slate-500 block">FMEA Type & Reference:</span>
            <div className="text-xs font-bold text-slate-900 mt-1">
              {fmea.fmeaType} — {fmea.documentNumber} ({fmea.revision})
            </div>
            <span className="text-[11px] text-slate-400 block mt-0.5">Assigned Owner: {fmea.assignedChangeOwner}</span>
          </div>

          <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50">
            <span className="text-[11px] text-slate-500 block">Associated Failure Mode:</span>
            <div className="text-xs font-bold text-slate-900 mt-1">
              {fmea.failureMode}
            </div>
            <span className="text-[11px] text-slate-400 block mt-0.5">Status: {fmea.status}</span>
          </div>

          <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between">
            <div>
              <span className="text-[11px] text-slate-500 block">AIAG/VDA Action Priority (AP):</span>
              <div className="text-xs font-bold text-slate-900 mt-1">
                S={fmea.currentSeverity} · O={fmea.currentOccurrence} · D={fmea.currentDetection}
              </div>
            </div>
            <span className="px-3 py-1 bg-rose-50 text-rose-700 border border-rose-200 font-bold text-xs rounded-lg shadow-sm">
              HIGH AP
            </span>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">
            FMEA Update Justification & Action Item:
          </label>
          <textarea
            rows={3}
            value={fmea.updateReason || ""}
            disabled={readOnly}
            onChange={(e) => setFmea(prev => ({ ...prev, updateReason: e.target.value }))}
            className="w-full text-xs p-3 rounded-lg border border-slate-300 bg-white text-slate-900 font-medium focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>
    </div>
  );
}
